import { queueRefusalText } from "../../lib/queue-refusal";
/**
 * alljourneys-walkins — the Walk-Ins tab's data ports.
 *
 * The Walk-Ins journey is a `type: "queue"` admin journey. Everything it stores
 * goes through the EXISTING typed admin journey door (listAdminJourneys /
 * getAdminJourney / createAdminJourney / updateAdminJourney /
 * setAdminJourneyStatus) — there is no second facade and no new admin endpoint.
 * The presentation-only block lives under the contract's `walkIn` key
 * (`WalkInJourneyConfig`, packages/api-contracts/src/walkin-journey.ts); the
 * header, footer, progress indicator, custom text and slug reuse the same
 * journey-level fields the Appointments tab already writes, so the two tabs
 * cannot disagree about what "hide the footer" means.
 *
 * What is DELIBERATELY not here:
 *   · no queue policy. Wait calculation, capacity, opening hours and which of
 *     `phone`/`email` a desk DEMANDS are properties of the queue row
 *     (`settings.joinForm`, resolved by `resolveQueueJoinForm`), and the join
 *     handler unions them with this journey's own requirements. This module
 *     never claims to change them.
 *   · no fabricated queue. `queueIds` is an author's explicit pick from the
 *     tenant's real `listQueues` page; a journey with none is not publishable
 *     as a walk-in page, and the tab says so rather than inventing a desk.
 *   · no synthesized ticket, position or wait. Every number on the customer's
 *     ticket comes from `joinPublicQueue` / `getPublicQueuePosition`.
 *
 * Every exported async function carries an EXPLICIT return annotation. Without
 * one, `tsc` has to name the inferred type of an api-client generic and emits
 * TS2742 ("cannot be named without a reference to ...") in this package.
 */
import {
  apiRaw,
  listAdminJourneys,
  getAdminJourney,
  createAdminJourney,
  updateAdminJourney,
  setAdminJourneyStatus,
  requestPublicSurface,
  queue as queueOps,
  publishing as publishingOps,
} from "@v0y4ge/api-client";
import { audienceRefusalCopy } from "../../lib/audience";
import { publicReadOptions } from "./appointment-data";
import { studioRequestOptions } from "../../lib/studio-config";
import { reportOpSource } from "../../shell/opSource";
import { getPublicQueue, listQueues, type PublicQueueView } from "../queues/queues-api";

import { t } from "@v0y4ge/i18n-runtime";
type Dict = Record<string, any>;

/** Studio preview vs the standalone customer page. Same vocabulary the
 *  appointment seam uses (`AppointmentMode`), for the same reason: the only
 *  thing that differs between the two mounts is where a read points and
 *  whether a write is real. */
export type WalkInMode = "preview" | "public";

/** The Gold Master default slug, singular. Kept EXACTLY as it is: a tenant that
 *  already published `join.<tenant>/walk-in` must not be silently repointed. */
export const WALKIN_DEFAULT_SLUG = "walk-in";

/** The presentation block as `WalkInJourneyConfig` declares it. Mirrored here
 *  (rather than importing the zod object) so this module stays a plain
 *  type-only consumer of the contract; the defaults below are the schema's own
 *  `.default(...)` values, field for field. */
export interface WalkInConfig {
  queueIds: string[];
  serviceDisplay: "cards" | "list";
  /** Show a flag beside each dial code in the phone field's country picker. */
  phoneCountryFlags: boolean;
  /** ISO alpha-2 codes pinned to the top of that picker (max 10). */
  phoneSuggestedCountries: string[];
  showCategories: boolean;
  showServiceImages: boolean;
  showMediaImages: boolean;
  qAskLocation: boolean;
  qWaitPerService: boolean;
  qShowTicket: boolean;
  qShowPos: boolean;
  qWaitDisplay: "range" | "exact" | "position";
  qAllowLeave: boolean;
  /** Public URL of the tenant's appointment booking journey, offered under
   *  "Leave the queue" as "Make an appointment instead". Empty = not offered.
   *  Pressing it LEAVES for real first; only a confirmed leave navigates. */
  qApptUrl: string;
  showQr: boolean;
  askEmail: boolean;
  emailOptional: boolean;
  askPhone: boolean;
  phoneOptional: boolean;
  qPartySize: boolean;
  qMaxParty: number;
}

export const WALKIN_CONFIG_DEFAULTS: WalkInConfig = {
  queueIds: [],
  serviceDisplay: "list",
  phoneCountryFlags: true,
  phoneSuggestedCountries: [],
  showCategories: true,
  showServiceImages: true,
  // Default ON (CC, 28 Sept): existing walk-in journeys keep their service images.
  showMediaImages: true,
  qAskLocation: false,
  qWaitPerService: false,
  qShowTicket: true,
  qShowPos: true,
  qWaitDisplay: "exact",
  qAllowLeave: true,
  qApptUrl: "",
  showQr: false,
  askEmail: false,
  emailOptional: true,
  askPhone: true,
  phoneOptional: true,
  qPartySize: false,
  qMaxParty: 1,
};

/* ── helpers ────────────────────────────────────────────────────────────── */

function isRecord(v: unknown): v is Dict {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function rec(v: unknown): Dict { return isRecord(v) ? v : {}; }
function str(v: unknown, fallback = ""): string { return typeof v === "string" ? v : fallback; }
function bool(v: unknown, fallback: boolean): boolean { return typeof v === "boolean" ? v : fallback; }
function ids(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x || "").trim()).filter(Boolean) : [];
}
function clampParty(n: unknown, fallback: number): number {
  const value = Math.round(Number(n));
  if (!Number.isFinite(value)) return fallback;
  return Math.min(20, Math.max(1, value));
}

/* ── designer state <-> contract ────────────────────────────────────────── */

/**
 * The Walk-Ins designer's own key names -> the `walkIn` block.
 *
 * The designer key names are the Gold Master's and are NOT renamed here: the
 * settings panels, the stash and every existing test speak them. This function
 * is the one place the two vocabularies meet.
 */
export function walkInConfigFromDesigner(s: Dict, queueIds: string[]): WalkInConfig {
  return {
    queueIds: ids(queueIds),
    // The designer offers Cards / List / Compact; the contract has two members,
    // and compact renders as a list on the customer page — same collapse the
    // appointment seam makes for `displayAs`.
    serviceDisplay: s.serviceDisplay === "grid" || s.serviceDisplay === "cards" || s.serviceDisplay === "tiles" ? "cards" : "list",
    /* Phone COUNTRY settings, bound rather than hidden (CD, 2026-09-17). The
       designer has always persisted `countryFlags` / `customCountries`; these
       are the contract's names for them on the walk-in block, so the walk-in
       phone field can render the same dial-code picker the appointment journey
       does. Flags default ON (the panel's own default) and the suggested list
       defaults empty; the picker caps it at ten, as the panel does. */
    phoneCountryFlags: s.countryFlags !== false,
    phoneSuggestedCountries: (Array.isArray(s.customCountries) ? s.customCountries : [])
      .map((c: unknown) => String(c).toUpperCase().trim())
      .filter((c: string) => c.length === 2)
      .slice(0, 10),
    showCategories: !!s.showCategories,
    showServiceImages: !!s.showServiceImages,
    showMediaImages: s.showMediaImages !== false,
    qAskLocation: !!s.queueLocationStep,
    qWaitPerService: !!s.liveWaitPerService,
    qShowTicket: !!s.showTicketNumber,
    qShowPos: !!s.showPosition,
    qWaitDisplay: s.waitDisplay === "range" ? "range" : s.waitDisplay === "position" ? "position" : "exact",
    qAllowLeave: !!s.allowLeaveQueue,
    qApptUrl: str(s.apptInsteadUrl).trim(),
    showQr: !!s.showQr,
    askEmail: !!s.askEmail,
    emailOptional: s.emailOptional !== false,
    askPhone: !!s.askPhone,
    phoneOptional: s.phoneOptional !== false,
    qPartySize: !!s.partySize,
    // `maxParty` only means anything when the party-size question is on; the
    // contract still bounds it to 1..20, and the JOIN HANDLER is what enforces
    // it. A client-side clamp here is presentation, never the guarantee.
    qMaxParty: s.partySize ? clampParty(s.maxParty, 1) : 1,
  };
}

/** A stored `walkIn` block (or nothing) -> a complete config. Absent or
 *  malformed reads as the schema's declared defaults, never as "off". */
export function walkInConfigFrom(raw: unknown): WalkInConfig {
  const w = rec(raw);
  return {
    queueIds: ids(w.queueIds),
    serviceDisplay: w.serviceDisplay === "cards" ? "cards" : WALKIN_CONFIG_DEFAULTS.serviceDisplay,
    phoneCountryFlags: bool(w.phoneCountryFlags, WALKIN_CONFIG_DEFAULTS.phoneCountryFlags),
    phoneSuggestedCountries: (Array.isArray(w.phoneSuggestedCountries) ? w.phoneSuggestedCountries : [])
      .map((c: unknown) => String(c).toUpperCase().trim())
      .filter((c: string) => c.length === 2)
      .slice(0, 10),
    showCategories: bool(w.showCategories, WALKIN_CONFIG_DEFAULTS.showCategories),
    showServiceImages: bool(w.showServiceImages, WALKIN_CONFIG_DEFAULTS.showServiceImages),
    showMediaImages: bool(w.showMediaImages, WALKIN_CONFIG_DEFAULTS.showMediaImages),
    qAskLocation: bool(w.qAskLocation, WALKIN_CONFIG_DEFAULTS.qAskLocation),
    qWaitPerService: bool(w.qWaitPerService, WALKIN_CONFIG_DEFAULTS.qWaitPerService),
    qShowTicket: bool(w.qShowTicket, WALKIN_CONFIG_DEFAULTS.qShowTicket),
    qShowPos: bool(w.qShowPos, WALKIN_CONFIG_DEFAULTS.qShowPos),
    qWaitDisplay: w.qWaitDisplay === "range" || w.qWaitDisplay === "position" || w.qWaitDisplay === "exact"
      ? w.qWaitDisplay : WALKIN_CONFIG_DEFAULTS.qWaitDisplay,
    qAllowLeave: bool(w.qAllowLeave, WALKIN_CONFIG_DEFAULTS.qAllowLeave),
    qApptUrl: str(w.qApptUrl, WALKIN_CONFIG_DEFAULTS.qApptUrl).trim(),
    showQr: bool(w.showQr, WALKIN_CONFIG_DEFAULTS.showQr),
    askEmail: bool(w.askEmail, WALKIN_CONFIG_DEFAULTS.askEmail),
    emailOptional: bool(w.emailOptional, WALKIN_CONFIG_DEFAULTS.emailOptional),
    askPhone: bool(w.askPhone, WALKIN_CONFIG_DEFAULTS.askPhone),
    phoneOptional: bool(w.phoneOptional, WALKIN_CONFIG_DEFAULTS.phoneOptional),
    qPartySize: bool(w.qPartySize, WALKIN_CONFIG_DEFAULTS.qPartySize),
    qMaxParty: clampParty(w.qMaxParty, WALKIN_CONFIG_DEFAULTS.qMaxParty),
  };
}

/**
 * A stored queue journey -> the designer keys it determines.
 *
 * Returns ONLY what the journey carries, exactly like `designerFromJourney` for
 * appointments, so the caller merges over its own defaults:
 * `{ ...QUEUE_DEFAULTS, ...designerFromWalkInJourney(journey).s }`.
 */
export function designerFromWalkInJourney(journey: Dict): { s: Dict; ct: Record<string, string>; queueIds: string[] } {
  const s: Dict = {};
  const ct: Record<string, string> = {};
  s.showHeader = rec(journey.header).show !== false;
  s.showFooter = rec(journey.footer).showCustom !== false;
  s.brandedHeader = rec(journey.header).branded === true;
  s.brandedFooter = rec(journey.footer).branded === true;
  if (typeof rec(journey.advanced).hideJrni === "boolean") s.hideJrni = rec(journey.advanced).hideJrni;

  const progress = rec(journey.progress);
  if ("show" in progress || "style" in progress) {
    const show = bool(progress.show, true);
    const style = str(progress.style, "titles");
    s.progressType = !show ? "none" : style === "segments" ? "numbers" : style === "bar" ? "bar" : "titles";
  }

  const w = journey.walkIn;
  if (isRecord(w)) {
    const cfg = walkInConfigFrom(w);
    s.serviceDisplay = cfg.serviceDisplay === "cards" ? "grid" : "list";
    /* READ THE COUNTRY SETTINGS BACK (CD, 2026-09-18). The outbound mapping and
       the public page reader were both correct; this block mapped every other
       `cfg.*` key and not these two, so after a reload the panel fell back to
       its defaults — and the next edit re-sent those defaults ALONGSIDE the
       change. A desk that pinned GB,FR came back showing US,MX; the author
       added DE and the stored list became US,MX,DE while the join page had
       been serving GB,FR the whole time. It reads as "the save didn't take",
       which is worse than losing the edit: the edit landed, on the wrong base. */
    s.countryFlags = cfg.phoneCountryFlags;
    s.customCountries = cfg.phoneSuggestedCountries;
    s.showCategories = cfg.showCategories;
    s.showServiceImages = cfg.showServiceImages;
    s.showMediaImages = cfg.showMediaImages;
    s.queueLocationStep = cfg.qAskLocation;
    s.liveWaitPerService = cfg.qWaitPerService;
    s.showTicketNumber = cfg.qShowTicket;
    s.showPosition = cfg.qShowPos;
    s.waitDisplay = cfg.qWaitDisplay;
    s.allowLeaveQueue = cfg.qAllowLeave;
    s.apptInsteadUrl = cfg.qApptUrl;
    s.showQr = cfg.showQr;
    s.askEmail = cfg.askEmail;
    s.emailOptional = cfg.emailOptional;
    s.askPhone = cfg.askPhone;
    s.phoneOptional = cfg.phoneOptional;
    s.partySize = cfg.qPartySize;
    s.maxParty = cfg.qMaxParty;
  }

  if (typeof journey.slug === "string" && journey.slug) s.slug = journey.slug;

  // Walk-In copy keys are `<group>.<field>` ids straight out of the designer's
  // own QUEUE_CT_GROUPS table, stored verbatim in the journey's `customText`
  // record. There is no step-heading re-homing here (unlike appointments): the
  // queue page's headings are not the appointment step vocabulary, and mapping
  // them onto it would put walk-in copy on an appointment step.
  const stored = rec(journey.customText);
  Object.keys(stored).forEach((k) => {
    const v = stored[k];
    if (typeof v === "string" && v !== "") ct[k] = v;
  });

  return { s, ct, queueIds: ids(rec(journey.walkIn).queueIds) };
}

export interface WalkInPatchOptions {
  /** What the tab was hydrated with (or last saved). Given, the patch carries
   *  only leaves that differ from it. */
  baseline?: { s: Dict; ct: Dict; queueIds: string[] } | null;
  /** The stored journey, so `customText` keys this designer does not own are
   *  carried rather than deleted by a wholesale replace. */
  journey?: Dict | null;
}

function fullWalkInPatch(s: Dict, ct: Dict, queueIds: string[], journey: Dict | null | undefined): Dict {
  const patch: Dict = {};
  if (typeof s.showHeader === "boolean") patch.header = { show: s.showHeader };
  if (typeof s.showFooter === "boolean") patch.footer = { showCustom: s.showFooter };
  if (typeof s.brandedHeader === "boolean") patch.header = { ...(patch.header as Dict || {}), branded: s.brandedHeader };
  if (typeof s.brandedFooter === "boolean") patch.footer = { ...(patch.footer as Dict || {}), branded: s.brandedFooter };
  if (typeof s.hideJrni === "boolean") patch.advanced = { hideJrni: s.hideJrni };

  const pt = String(s.progressType || "titles");
  const storedProgress = rec(rec(journey).progress);
  const storedStyle = str(storedProgress.style);
  const style = pt === "numbers" ? "segments" : pt === "bar" ? "bar" : pt === "titles" ? "titles"
    : (storedStyle === "bar" || storedStyle === "segments" || storedStyle === "titles" ? storedStyle : "titles");
  patch.progress = { show: pt !== "none", style };
  if (pt === "numbers" && storedProgress.style !== "segments") patch.progress.showCount = true;
  else if (typeof storedProgress.showCount === "boolean") patch.progress.showCount = storedProgress.showCount;

  patch.walkIn = walkInConfigFromDesigner(s, queueIds);
  // `WalkInJourneyConfig.queueIds` is `.min(1)`, and the update door takes a
  // PARTIAL of it. An author who has not attached a desk yet must still be able
  // An empty selection is SENT as `[]`, not omitted.
  //
  // Omitting it was worse than a refused save: `walkIn` is a DEEP-MERGE key, so
  // a patch without `queueIds` leaves whatever is already stored in place. An
  // author who detached every desk and pressed Save was told "Saved" while the
  // published page carried on offering the queues they had just removed. The
  // schema's `.min(1)` is what refuses this, and `saveWalkInSettings` refuses it
  // one step earlier with a message that names the fix.

  // `customText` is REPLACED wholesale by the server, so it is rebuilt in full:
  // stored keys this designer does not own are carried, its own keys are
  // rewritten, and a cleared field is a deletion.
  const copy: Record<string, string> = {};
  const storedCopy = rec(rec(journey).customText);
  Object.keys(storedCopy).forEach((k) => {
    const v = storedCopy[k];
    if (typeof v === "string" && !(k in (ct || {}))) copy[k] = v;
  });
  Object.keys(ct || {}).forEach((k) => {
    const v = ct[k];
    if (typeof v === "string" && v.trim() !== "") copy[k] = v;
  });
  if (Object.keys(copy).length || Object.keys(storedCopy).length) patch.customText = copy;

  if (typeof s.slug === "string" && s.slug.trim() !== "") patch.slug = s.slug.trim();
  return patch;
}

function diffLeaves(next: Dict, prev: Dict): Dict {
  const out: Dict = {};
  Object.keys(next).forEach((k) => {
    const n = next[k]; const p = prev[k];
    if (isRecord(n) && isRecord(p)) {
      const inner = diffLeaves(n, p);
      if (Object.keys(inner).length) out[k] = inner;
      return;
    }
    if (JSON.stringify(n) !== JSON.stringify(p)) out[k] = n;
  });
  return out;
}

/**
 * The designer state -> an `updateAdminJourney` body with only changed leaves.
 *
 * `walkIn` is on the contract's DEEP-MERGE list, so a partial block is legal
 * and merges onto the stored one. `queueIds` is an ARRAY and therefore replaces
 * wholesale, which is what an author picking a different set of desks means.
 */
export function buildWalkInPatch(s: Dict, ct: Dict = {}, queueIds: string[] = [], opts: WalkInPatchOptions = {}): Dict {
  const next = fullWalkInPatch(s, ct, queueIds, opts.journey);
  if (!opts.baseline) return next;
  const prev = fullWalkInPatch(opts.baseline.s, opts.baseline.ct, opts.baseline.queueIds || [], opts.journey);
  const patch = diffLeaves(next, prev);
  if (JSON.stringify(next.customText) !== JSON.stringify(prev.customText)) {
    patch.customText = next.customText ?? {};
  }
  if (patch.slug !== undefined) patch.movePublicLink = true;
  return patch;
}

/* ── Refusals ───────────────────────────────────────────────────────────── */

export interface WalkInRefusedField { path: string; message: string }
export interface WalkInSaveRefusal {
  kind: "config-invalid" | "envelope-invalid" | "not-found" | "conflict" | "unavailable";
  code: string;
  status: number;
  message: string;
  fields: WalkInRefusedField[];
}
export type WalkInSaveResult =
  | { ok: true; journey: Dict; noop?: boolean }
  | { ok: false; refusal: WalkInSaveRefusal };

export function walkInRefusalFrom(err: unknown): WalkInSaveRefusal {
  const e = (err || {}) as Dict & { fields?: Array<Dict>; status?: number; code?: string; message?: string; name?: string };
  if (e.name === "ZodError") {
    return {
      kind: "unavailable", code: "RESPONSE_SHAPE", status: 0, fields: [],
      message: t("journeys.alljourneysWalkins.message", "The server answered in a shape this screen does not recognise; the save may or may not have landed. Reload to check."),
    };
  }
  const status = Number(e.status || 0);
  const code = String(e.code || (e.body && e.body.error && e.body.error.code) || "UNKNOWN");
  const rawFields = Array.isArray(e.fields)
    ? e.fields
    : (e.body && e.body.error && Array.isArray(e.body.error.fields) ? e.body.error.fields : []);
  const kind: WalkInSaveRefusal["kind"] =
    code === "JOURNEY_CONFIG_INVALID" || status === 422 ? "config-invalid"
      : code === "VALIDATION_ERROR" || status === 400 ? "envelope-invalid"
        : status === 404 ? "not-found"
          : status === 409 ? "conflict" : "unavailable";
  return {
    kind, code, status,
    message: String(e.message || t("journeys.alljourneysWalkins.saveRefused", "The server refused this save.")),
    fields: rawFields.map((f: Dict) => ({ path: String(f.path || ""), message: String(f.message || "") })),
  };
}

/* ── The admin door ─────────────────────────────────────────────────────── */

/** GET /v1/admin/journeys -> the tenant's queue journey row, preferring the
 *  given slug. Returns the ROW (id, slug, status…), not just an id. */
export async function resolveWalkInJourney(preferredSlug?: string): Promise<Dict | null> {
  const res = await listAdminJourneys();
  reportOpSource("listAdminJourneys", res);
  const rows = (res && Array.isArray(res.journeys) ? res.journeys : []) as Array<Dict>;
  const queues = rows.filter((r) => String(r.type || "") === "queue");
  const wanted = String(preferredSlug || "").trim();
  const bySlug = wanted ? queues.filter((r) => String(r.slug || "") === wanted)[0] : null;
  return bySlug || queues[0] || null;
}

/** GET /v1/admin/journeys/{id} — the full stored config, for hydration. */
export async function loadWalkInJourney(journeyId: string): Promise<Dict> {
  const journey = (await getAdminJourney(journeyId)) as unknown as Dict;
  reportOpSource("getAdminJourney", journey);
  return journey;
}

/**
 * POST /v1/admin/journeys — a queue journey from the `qclassic` starter.
 *
 * `qclassic` is the queue starter layout the create door already knows
 * (`JOURNEY_STARTER_LAYOUTS`); the server refuses an id it does not recognise
 * with `JOURNEY_TEMPLATE_UNSUPPORTED`, so this is not a free-form string.
 */
export async function createWalkInJourney(name: string): Promise<WalkInSaveResult> {
  try {
    const journey = (await createAdminJourney({ templateId: "qclassic", name, type: "queue" })) as unknown as Dict;
    reportOpSource("createAdminJourney", journey);
    return { ok: true, journey };
  } catch (err) {
    return { ok: false, refusal: walkInRefusalFrom(err) };
  }
}

/**
 * The one refusal this module raises WITHOUT asking the server.
 *
 * A walk-in page with no queue attached is not a saveable state: `walkIn` is a
 * deep-merge key, so a patch carrying `queueIds: []` is refused by the schema
 * (`.min(1)`) and a patch OMITTING it silently keeps the queues that are
 * already published. Neither outcome may be reported as a save, so the tab is
 * told no here, keeps its dirty draft, and is given the fix in words.
 */
export function walkInNoQueueRefusal(): WalkInSaveRefusal {
  return {
    kind: "config-invalid",
    code: "WALKIN_NO_QUEUE",
    status: 0,
    message: t("journeys.alljourneysWalkins.message2", "Pick at least one queue before saving. A walk-in page with no line attached cannot be stored, and leaving it empty would keep whatever is already published."),
    fields: [{ path: "walkIn.queueIds", message: t("journeys.alljourneysWalkins.message3", "Attach at least one queue in General.") }],
  };
}

/** PATCH /v1/admin/journeys/{id} with a changed-leaves-only body. An empty diff
 *  is a no-op: nothing is sent and the stored journey is returned. */
export async function saveWalkInSettings(
  journeyId: string, s: Dict, ct: Dict = {}, queueIds: string[] = [], opts: WalkInPatchOptions = {},
): Promise<WalkInSaveResult> {
  if (!ids(queueIds).length) return { ok: false, refusal: walkInNoQueueRefusal() };
  const body = buildWalkInPatch(s, ct, queueIds, opts);
  try {
    if (Object.keys(body).length === 0) {
      const current = opts.journey || (await loadWalkInJourney(journeyId));
      return { ok: true, journey: current, noop: true };
    }
    const journey = (await updateAdminJourney(journeyId, body as Parameters<typeof updateAdminJourney>[1])) as unknown as Dict;
    reportOpSource("updateAdminJourney", journey);
    return { ok: true, journey };
  } catch (err) {
    return { ok: false, refusal: walkInRefusalFrom(err) };
  }
}

/** POST /v1/admin/journeys/{id}/status — publish is a status transition over
 *  whatever was last SAVED, never a second write of the draft. */
export async function publishWalkInJourney(journeyId: string): Promise<WalkInSaveResult> {
  try {
    const journey = (await setAdminJourneyStatus(journeyId, { status: "published" })) as unknown as Dict;
    reportOpSource("setAdminJourneyStatus", journey);
    return { ok: true, journey };
  } catch (err) {
    return { ok: false, refusal: walkInRefusalFrom(err) };
  }
}

/* ── The queue picker ───────────────────────────────────────────────────── */

export interface WalkInQueueOption {
  id: string;
  name: string;
  locationId: string;
  locationName: string;
  status: "open" | "paused" | "closed";
  services: string[];
  /** The queue row's creation instant, verbatim ("" when absent) — picks the builder's sample queues. */
  createdAt: string;
}

/**
 * Every queue on the tenant, for the author's explicit pick.
 *
 * PAGINATED, and followed to the end: `listQueues` answers `{ data, pagination }`
 * and a tenant with more desks than one page would otherwise be silently
 * truncated — an author would look at a complete-looking list and not find the
 * desk they were told to attach. The page cursor is the contract's own
 * `pagination.hasMore` / `page`, and the walk is bounded so a server that never
 * clears `hasMore` cannot spin here.
 */
export async function listWalkInQueueOptions(): Promise<WalkInQueueOption[]> {
  const out: WalkInQueueOption[] = [];
  const seen = new Set<string>();
  const take = (rows: Array<Dict>) => {
    for (const raw of rows) {
      const row = rec(raw);
      const id = str(row.id);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      // The worker keeps the studio-facing fields inside the settings blob and
      // the foreign keys under their column names — the same two places
      // `normalizeQueueSummary` reads. Anything absent stays EMPTY.
      const settings = rec(row.settings);
      const status = str(row.status);
      out.push({
        id,
        name: str(row.name),
        locationId: str(row.locationId) || str(row.location_id),
        locationName: str(row.locationName) || str(settings.locationName),
        status: status === "open" || status === "paused" || status === "closed" ? status : "closed",
        services: Array.isArray(row.services) ? row.services.map(String)
          : Array.isArray(settings.services) ? settings.services.map(String) : [],
        createdAt: str(row.createdAt) || str(row.created_at),
      });
    }
  };

  // Page one through the module every other queue screen uses, so the first
  // page is normalized exactly as they see it.
  const first = await listQueues();
  take((first.data || []) as unknown as Array<Dict>);
  let pagination = first.pagination;
  // A tenant with more desks than one page would otherwise be silently
  // truncated, and an author would look at a complete-looking list without
  // finding the desk they were told to attach. Bounded so a server that never
  // clears `hasMore` cannot spin here.
  for (let guard = 0; guard < 50 && pagination && pagination.hasMore; guard += 1) {
    const next = Number(pagination.page || 1) + 1;
    const limit = Number(pagination.limit || 0);
    let body: Dict | null = null;
    try {
      body = (await apiRaw<Dict>(
        "/v1/admin/queues?page=" + next + (limit ? "&limit=" + limit : ""),
        undefined,
        "listQueues",
      )) as Dict;
    } catch {
      break;
    }
    const rows = Array.isArray(rec(body).data) ? (rec(body).data as Array<Dict>) : [];
    if (!rows.length) break;
    take(rows);
    pagination = rec(body).pagination as typeof pagination;
  }
  return out;
}

/* ── The public door ────────────────────────────────────────────────────── */

/**
 * Where an explicit-base public read points — identical rule to
 * `publicReadOptions` in appointment-data.ts, and deliberately NOT a call to
 * `configureApiClient`: this component also runs inside Studio, and repointing
 * the global client from it would repoint every other screen's requests.
 */
export function walkInPublicReadOptions(mode: WalkInMode): { baseUrl: string } {
  return mode === "public" ? { baseUrl: "" } : { baseUrl: studioRequestOptions().baseUrl };
}

/** The slug a standalone join page is serving, from the first path segment. */
export function walkInSlugFromPathname(pathname: string): string {
  const parts = String(pathname || "").split("/").filter(Boolean);
  if (parts.length === 0) return WALKIN_DEFAULT_SLUG;
  try { return decodeURIComponent(parts[0]); } catch { return parts[0]; }
}

export interface PublishedWalkInJourney {
  slug: string;
  name: string;
  brandColor: string;
  logoText: string;
  showHeader: boolean;
  showFooter: boolean;
  brandedHeader: boolean;
  brandedFooter: boolean;
  hideJrni: boolean;
  footer: Dict;
  progressType: "none" | "bar" | "numbers" | "titles";
  progressShowCount: boolean;
  customText: Record<string, string>;
  walkIn: WalkInConfig;
}

/**
 * The journey's OWN brand — the one thing the designer preview and the
 * standalone page must not each work out for themselves.
 *
 * They did, and it showed: the public page read the persisted
 * `configJson.brand` (whatever the queue starter stored) while the preview
 * used the Studio shell's own tenant name and tenant colour, so the same
 * journey had two different headers depending on which surface you looked at.
 * Neither was wrong on its own; having two sources was.
 *
 * The journey record is that one source, and both surfaces already hold it —
 * the designer from `getAdminJourney` (flat) and the page from the public
 * surface's `configJson`. Nothing new is read, nothing is invented, and no
 * tenant is named in code: an unbranded journey resolves to "" here and the
 * renderer supplies the DS token / neutral wording, identically for both.
 *
 * NOT the Studio shell's tenant brand. That is an authenticated read a public
 * visitor cannot make, so preferring it in the designer guarantees the two
 * surfaces disagree.
 */
export function walkInBrandFrom(config: unknown): { brandColor: string; logoText: string } {
  const j = rec(config);
  const brand = rec(j.brand);
  return {
    brandColor: str(brand.color) || str(rec(rec(j.brandTokens).colors).primary),
    logoText: str(brand.logoText) || str(brand.name),
  };
}

/**
 * The published surface for a slug, projected to what the customer page draws.
 *
 * Returns **null** for every "there is no walk-in page here" case — no slug, an
 * unknown slug, a journey that is not published, and a journey that is
 * published but is not `type: "queue"`. The caller renders a content-free
 * not-found for all of them: a wrong slug must not reveal that the tenant has
 * an appointment journey by that name, nor that a draft exists.
 *
 * The transport is `requestPublicSurface` on the SAME path the appointment page
 * reads (`loadPublishedJourney` in appointment-data.ts). There is no
 * `getPublicJourneyBySlug` operation — the registered operation behind this
 * route is the public journey surface read, and inventing a second call id for
 * the same (method, path) is the competing-handler split the contract forbids.
 */
export async function loadPublishedWalkInJourney(slug: string, mode: WalkInMode): Promise<PublishedWalkInJourney | null> {
  if (!slug) return null;
  const surface = await requestPublicSurface<Dict>(
    "/v1/journeys/public/" + encodeURIComponent(slug),
    { method: "GET" },
    walkInPublicReadOptions(mode),
    true,
  );
  return projectPublishedWalkInJourney(surface);
}

/**
 * The projection itself, split out so it is testable without a transport.
 *
 * THE SURFACE IS NOT THE CONFIG. `GET /v1/journeys/public/{slug}` answers
 * `mapJourneyRow`'s shape — `{ id, journeyId, version, status, isLive, slug,
 * configType, configJson, publicPath, publicUrl, … }` — and the journey's own
 * settings (`type`, `walkIn`, `header`, `footer`, `progress`, `customText`,
 * `brand`) live INSIDE `configJson`. `requestPublicSurface` unwraps the `data`
 * envelope and nothing else. Reading `surface.type` / `surface.walkIn` at the
 * top level therefore matched nothing on a real published page and this
 * function returned null for every one of them — the standalone join page
 * rendered its content-free not-found against a page that was correctly
 * published.
 *
 * The admin door is the other shape: `toAdminJourneySummary` SPREADS the config
 * at the top level and says `status: "published"`. Both are accepted here, from
 * one rule — take `configJson` when it is there, otherwise the object itself —
 * because the designer and the customer page project the same journey.
 */
export function projectPublishedWalkInJourney(surface: unknown): PublishedWalkInJourney | null {
  const surf = rec(surface);
  if (!Object.keys(surf).length) return null;
  const j = isRecord(surf.configJson) ? surf.configJson : surf;

  // `configType` is the ROW's column and is authoritative; `configJson.type` is
  // the same value inside the config. Either naming a non-queue journey is
  // enough to refuse — an appointment page on this slug must read as "no page
  // here", not as an empty walk-in page.
  const type = str(surf.configType) || str(j.type);
  if (type !== "queue") return null;

  // Published only. The public surface says `status: "live"` / `isLive: true`;
  // the admin summary says `status: "published"`. A surface that carries only
  // one of them still cannot leak a draft, and an explicit draft is refused
  // even if some other field disagrees.
  const status = str(surf.status) || str(j.status);
  if (status === "draft") return null;
  // `isLive` is the ROW's own boolean and is decisive when present: the config
  // inside carries its own `status` copy, which a publish/unpublish cycle can
  // leave stale, and trusting that copy over the row is how an unpublished
  // page keeps serving.
  const live = typeof surf.isLive === "boolean"
    ? surf.isLive
    : (surf.isLive === 1 || status === "published" || status === "live");
  if (!live) return null;

  const walkIn = walkInConfigFrom(j.walkIn);
  if (!walkIn.queueIds.length) return null;

  const progress = rec(j.progress);
  const style = str(progress.style, "titles");
  const progressType = bool(progress.show, true)
    ? (style === "segments" ? "numbers" : style === "bar" ? "bar" : "titles")
    : "none";

  const customText: Record<string, string> = {};
  const stored = rec(j.customText);
  Object.keys(stored).forEach((k) => { if (typeof stored[k] === "string" && stored[k]) customText[k] = stored[k]; });

  return {
    // The ROW's slug is the one the page is being served under; the config
    // carries a copy that a rename can leave stale.
    slug: str(surf.slug) || str(j.slug),
    name: str(j.name),
    ...walkInBrandFrom(j),
    showHeader: rec(j.header).show !== false,
    showFooter: rec(j.footer).showCustom !== false,
    brandedHeader: rec(j.header).branded === true,
    brandedFooter: rec(j.footer).branded === true,
    hideJrni: rec(j.advanced).hideJrni === true,
    footer: rec(j.footer),
    progressType: progressType as PublishedWalkInJourney["progressType"],
    progressShowCount: bool(progress.showCount, false),
    customText,
    walkIn,
  };
}

/* ── The queues a walk-in page offers ───────────────────────────────────── */

/**
 * One service a queue offers, AS THE QUEUE RESOLVED IT.
 *
 * `id` is the canonical service reference that goes back on the join;
 * `name` is what a visitor reads. They are separate fields because they are
 * separate things: the public projection's `serviceOptions` resolves the
 * queue's configured refs against the catalog and returns both, plus the real
 * `category` the service belongs to and its `image`.
 *
 * There is NO label-derived variant of this. See `serviceOptionsOf`.
 */
export interface WalkInServiceOption {
  id: string;
  name: string;
  category: string;
  image: string;
  /** "15 min" — present once the public queue view carries a duration
   *  (CD-API-REQUEST-queue-service-options-duration-description-2026-09-16).
   *  "" until then; the card simply omits the line. */
  duration: string;
  description: string;
  /** Resolved booking questions for this service (JAI-1911). Empty when none. */
  questions: WalkInQuestion[];
}

/** One served question, as the public queue view serves it (PublicBookingQuestion). */
export interface WalkInQuestion {
  id: string;
  label: string;
  type: string;
  required: boolean;
  help: string;
  placeholder: string;
  options: { id: string; label: string }[];
  /** `rows` are the counter's rows as served; a stepper answer is a map of row id → whole count (CC 0065). */
  stepper: { min?: number; max?: number; step?: number; allowMultiple?: boolean; rows?: { id: string; label?: string; min?: number; max?: number }[] } | null;
}

/** One typed walk-in answer (QueueJoinAnswer): the question id and its value in the question's own type. */
export type WalkInAnswer = { questionId: string; value: string | number | boolean | null | string[] | Record<string, number> };

function questionsOf(raw: unknown): WalkInQuestion[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(rec).filter((q) => str(q.id) !== "" && str(q.label) !== "").map((q) => ({
    id: str(q.id),
    label: str(q.label),
    type: str(q.type) || "short",
    required: q.required === true,
    help: str(q.help),
    placeholder: str(q.placeholder),
    options: Array.isArray(q.options) ? q.options.map(rec).filter((o) => str(o.id) !== "").map((o) => ({ id: str(o.id), label: str(o.label) || str(o.id) })) : [],
    stepper: q.stepper && typeof q.stepper === "object" ? (q.stepper as WalkInQuestion["stepper"]) : null,
  }));
}

/** The queue's join form as `resolveQueueJoinForm` returns it: two fields, each
 *  `{ enabled, required }`, with a disabled field never required. */
export interface WalkInJoinForm {
  email: { enabled: boolean; required: boolean };
  phone: { enabled: boolean; required: boolean };
}

/** The queue's own live capacity answer (`getPublicQueueStatus`). Separate from
 *  the projection's `status` word: a queue can be `open` and still refuse
 *  entries because the store is shut or it is at capacity, and only this read
 *  says so. */
export interface WalkInQueueStatus {
  canAcceptEntries: boolean;
  storeOpen: boolean;
  nextOpenTime: string;
  /** The reopening as an ISO-8601 UTC instant, or "" when the desk is open or
   *  unrestricted — or when the worker is older than this field. `nextOpenTime`
   *  stays the fallback for exactly that case. */
  nextOpenAt: string;
  /** The VENUE's IANA zone (e.g. "America/Chicago"), or "" in the same cases.
   *  Without it a reopening time is ambiguous: "Opens 9am" read on a phone in
   *  another zone is a different moment from the one the desk means. */
  timezone: string;
  waiting: number;
  estWaitMins: number;
}

export interface WalkInQueueRead {
  id: string;
  /** null when the read failed or the queue is gone — never a placeholder. */
  view: PublicQueueView | null;
  /** null when the status read did not happen or failed; the caller then falls
   *  back to the projection's own `status`, and never to an optimistic yes. */
  status: WalkInQueueStatus | null;
  error: string;
}

/**
 * One queue's canonical public projection plus its live capacity.
 *
 * TWO reads, because they answer different questions. `getPublicQueue` gives
 * the page its content — name, org, the resolved `serviceOptions`, the
 * `joinForm` this desk enforces, per-service waits. `getPublicQueueStatus`
 * gives the one fact the projection cannot: whether the desk will take someone
 * *right now* (`can_accept_entries`, `store_open`, `next_open_time`). A page
 * that inferred "open" from the projection alone offered a Join button outside
 * opening hours and let the server say no after the visitor had typed their
 * details in.
 *
 * The status read is best-effort: it never fails the queue, because the
 * projection is still worth drawing without it.
 */
export async function loadWalkInQueue(queueId: string, entryId?: string | null): Promise<WalkInQueueRead> {
  let view: PublicQueueView | null = null;
  let error = "";
  try {
    const raw = (await getPublicQueue(queueId, entryId || null)) as unknown as Dict;
    // The worker serves this projection at the TOP level and says so in its own
    // comment; the contract schema wraps it in `data`. Accept both rather than
    // pick one and be wrong on whichever surface disagrees.
    const candidate = (isRecord(raw) && isRecord(raw.queue) ? raw : rec(raw).data) as unknown as PublicQueueView;
    if (candidate && isRecord((candidate as unknown as Dict).queue)) view = candidate;
    else error = t("journeys.alljourneysWalkins.servicePointLoadFailed", "This service point could not be loaded.");
  } catch (err) {
    error = publicErrorMessage(err, t("journeys.alljourneysWalkins.servicePointLoadFailed", "This service point could not be loaded."));
  }
  if (!view) return { id: queueId, view: null, status: null, error };
  return { id: queueId, view, status: await loadWalkInQueueStatus(queueId), error: "" };
}

/** `GET /v1/public/queues/{id}/status`, camelized by the api-client. Returns
 *  null rather than a guess when the read is unavailable. */
export async function loadWalkInQueueStatus(queueId: string): Promise<WalkInQueueStatus | null> {
  try {
    const raw = (await queueOps.getPublicQueueStatus(queueId)) as unknown as Dict;
    const body = isRecord(raw.data) ? rec(raw.data) : rec(raw);
    if (!Object.keys(body).length) return null;
    return {
      // Absent reads as "cannot", not as "can": the whole point of this read is
      // to stop the page promising a join the desk will refuse.
      canAcceptEntries: body.canAcceptEntries === true || body.can_accept_entries === true,
      storeOpen: body.storeOpen !== false && body.store_open !== false,
      nextOpenTime: str(body.nextOpenTime) || str(body.next_open_time),
      nextOpenAt: str(body.nextOpenAt) || str(body.next_open_at),
      timezone: str(body.timezone) || str(body.time_zone),
      waiting: Number(body.waiting) || 0,
      estWaitMins: Number(body.estimatedWaitMinutes ?? body.estimated_wait_minutes) || 0,
    };
  } catch {
    return null;
  }
}

/** Every assigned queue, read in parallel. A queue that fails to read is kept
 *  in the list WITH its error rather than dropped, so the page can say which
 *  service point is unavailable instead of quietly offering fewer. */
export async function loadWalkInQueues(queueIds: string[], entryByQueue?: Record<string, string>): Promise<WalkInQueueRead[]> {
  const list = ids(queueIds);
  if (!list.length) return [];
  return Promise.all(list.map((id) => loadWalkInQueue(id, (entryByQueue || {})[id] || null)));
}

/**
 * The services a queue actually offers, resolved.
 *
 * ONLY `serviceOptions` — the queue's configured refs resolved against the
 * catalog by the worker, which is the only place a canonical service id comes
 * from. When the projection carries none, this returns NOTHING and the page
 * shows its unavailable state.
 *
 * THERE IS NO LABEL FALLBACK, deliberately. `queue.services` is a DISPLAY list;
 * the worker builds `serviceOptions` from it by matching each ref against
 * `services.id OR services.name`, so a queue configured by name has entries
 * whose id and label differ. Offering the label as a selectable value therefore
 * submitted a display string where the join expects a catalog id — accepted by
 * the schema (it is just a bounded string) and then either attached to the
 * wrong service or silently dropped to the queue's default. An empty list is
 * the honest answer: the catalog did not resolve, so there is nothing this page
 * can truthfully offer.
 */
export function serviceOptionsOf(view: PublicQueueView | null | undefined): WalkInServiceOption[] {
  const q = rec(rec(view).queue);
  const options = Array.isArray(q.serviceOptions) ? (q.serviceOptions as Array<Dict>) : [];
  return options
    .filter((row) => isRecord(row) && str(row.id))
    .map((row) => ({
      id: str(row.id),
      name: str(row.name) || str(row.id),
      category: str(row.category),
      image: str(row.image),
      duration: typeof row.durationMinutes === "number" && row.durationMinutes > 0 ? row.durationMinutes + " min" : str(row.duration),
      description: str(row.description),
      questions: questionsOf(row.questions),
    }));
}

/** @deprecated Reads the flat label list. Kept only for callers that genuinely
 *  want the queue's raw `services` strings; anything that renders or submits a
 *  service must use `serviceOptionsOf`. */
export function servicesOf(view: PublicQueueView | null | undefined): string[] {
  const q = rec(view).queue;
  return Array.isArray(rec(q).services) ? rec(q).services.map(String) : [];
}

/**
 * The join form THIS DESK enforces.
 *
 * Mirrors `resolveQueueJoinForm`'s contract: two fields, `{enabled, required}`
 * each, a disabled field never required, and an absent block reading as the
 * platform defaults (`QUEUE_JOIN_FORM_DEFAULTS`: phone offered but optional,
 * email not asked).
 */
export function joinFormOf(view: PublicQueueView | null | undefined): WalkInJoinForm {
  const raw = rec(rec(rec(view).queue).joinForm);
  const field = (value: unknown, fallback: { enabled: boolean; required: boolean }) => {
    const f = rec(value);
    if (typeof f.enabled !== "boolean") return fallback;
    return f.enabled ? { enabled: true, required: f.required === true } : { enabled: false, required: false };
  };
  return {
    email: field(raw.email, { enabled: false, required: false }),
    phone: field(raw.phone, { enabled: true, required: false }),
  };
}

export interface WalkInFieldRule { show: boolean; required: boolean; /** the queue, not this page, is why */ enforcedByQueue: boolean }

/**
 * What the form actually asks for: the UNION of this page's settings and the
 * chosen queue's own form.
 *
 * The join handler validates against exactly that union (`journeyJoinSchema`
 * refines `queueJoinRequestFor(queueSettings)`), so anything narrower here is a
 * form that gets refused after the visitor has filled it in. In particular a
 * journey CANNOT hide or relax a field the queue requires — `askPhone: false`
 * on a desk whose `joinForm.phone.required` is true still shows the field, and
 * still marks it required.
 */
export function effectiveJoinFields(config: WalkInConfig, view: PublicQueueView | null | undefined): { email: WalkInFieldRule; phone: WalkInFieldRule } {
  const queueForm = joinFormOf(view);
  const merge = (ask: boolean, optional: boolean, rule: { enabled: boolean; required: boolean }): WalkInFieldRule => {
    const required = (ask && !optional) || rule.required;
    return {
      // A required field is always shown: a required-but-hidden field is a form
      // that cannot be submitted.
      show: ask || rule.enabled || required,
      required,
      enforcedByQueue: rule.required && !(ask && !optional),
    };
  };
  return {
    email: merge(config.askEmail, config.emailOptional, queueForm.email),
    phone: merge(config.askPhone, config.phoneOptional, queueForm.phone),
  };
}

/**
 * Whether a queue will take someone right now.
 *
 * The live status read is the authority when it is available: a queue whose
 * projection says `open` can still be outside its hours or at capacity, and
 * `can_accept_entries` is the field that knows. Without that read the
 * projection's own `status` is the fallback — never an optimistic yes.
 */
export function queueAcceptsJoins(view: PublicQueueView | null | undefined, status?: WalkInQueueStatus | null): boolean {
  if (status) return status.canAcceptEntries;
  return String(rec(rec(view).queue).status || "") === "open";
}

/** The reopening, on the VENUE's clock, named so it cannot be misread.
 *
 *  "Opens 9:00 AM (Chicago time)" — and when the visitor's own zone differs,
 *  "(that's 7:30 AM your time)" from the same instant, because a traveller or
 *  a remote helper reading "9am" has no way to know whose 9am it is
 *  (CD, 2026-09-18). The weekday appears only when the reopening is not today,
 *  so a desk reopening this afternoon does not read as a diary entry.
 *
 *  Returns "" when there is no instant or no zone — the caller then falls back
 *  to the worker's own `next_open_time` string, which is what an older worker
 *  sends and all it sends. */
function reopeningOnVenueClock(iso: string, timeZone: string): string {
  if (!iso || !timeZone) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  try {
    /** The calendar date at a given zone, as YYYY-MM-DD, so "is it today
     *  there?" is a string compare rather than offset arithmetic. */
    const dayAt = (tz: string, when: Date) =>
      new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(when);
    const now = new Date();
    const timeAt = (tz: string) => new Intl.DateTimeFormat(undefined, {
      timeZone: tz, hour: "numeric", minute: "2-digit",
      ...(dayAt(tz, at) === dayAt(tz, now) ? {} : { weekday: "short" as const }),
    }).format(at);

    const time = timeAt(timeZone);
    /* The zone's own city, not the IANA path: "Chicago time" is what a visitor
       recognises; "America/Chicago time" is a database key. */
    const city = timeZone.split("/").pop()?.replace(/_/g, " ") || timeZone;
    let out = t("journeys.alljourneysWalkins.zoneTime", "{time} ({city} time)", { time, city });
    let localZone = "";
    try { localZone = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { localZone = ""; }
    if (localZone && localZone !== timeZone) {
      const mine = timeAt(localZone);
      if (mine !== time) out = t("journeys.alljourneysWalkins.zoneTimeYours", "{label} — that's {localTime} your time", { label: out, localTime: mine });
    }
    return out;
  } catch {
    return "";
  }
}

/** "6:00" / "18:30" → the locale's own time-of-day ("6:00 AM"). A wall-clock
 *  label with no date, so no zone conversion; anything else passes through. */
function wallClockLabel(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(hhmm || "").trim());
  if (!m) return String(hhmm || "");
  try {
    return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", timeZone: "UTC" }).format(new Date(Date.UTC(2000, 0, 1, Number(m[1]), Number(m[2]))));
  } catch { return String(hhmm); }
}

/** Why a queue is not taking anyone, in the visitor's words. "" when it is. */
export function queueClosedReason(view: PublicQueueView | null | undefined, status?: WalkInQueueStatus | null): string {
  if (queueAcceptsJoins(view, status)) return "";
  if (status && !status.storeOpen) {
    const onVenueClock = reopeningOnVenueClock(status.nextOpenAt, status.timezone);
    if (onVenueClock) return t("journeys.alljourneysWalkins.closedOpensAt", "Closed right now. Opens {time}.", { time: onVenueClock });
    /* A bare "HH:mm" reads "Opens 6:00" beside "6:00 AM" everywhere else
       (2026-09-24 intake #14) — formatted through the same locale clock. */
    return status.nextOpenTime ? t("journeys.alljourneysWalkins.closedOpensAt", "Closed right now. Opens {time}.", { time: wallClockLabel(status.nextOpenTime) }) : t("journeys.alljourneysWalkins.closedNow", "Closed right now.");
  }
  const word = String(rec(rec(view).queue).status || "");
  if (word === "paused") return t("journeys.alljourneysWalkins.linePaused", "This line is paused and is not taking anyone at the moment.");
  if (word === "closed") return t("journeys.alljourneysWalkins.lineClosed", "This line is closed.");
  return t("journeys.alljourneysWalkins.lineNotTaking", "This line is not taking anyone at the moment.");
}

/* ── Join / poll / leave ────────────────────────────────────────────────── */

export interface WalkInJoinInput {
  queueId: string;
  /** The configured journey slug. REQUIRED on the native page: its presence is
   *  what opts the join into the published journey's allowed queues and its
   *  customer-form policy (workers/queue `getLiveWalkInJourney`). Omitting it
   *  falls back to the legacy queue-only door, which does not know this page. */
  journeySlug: string;
  name: string;
  phone?: string;
  /** ISO alpha-2 region for `phone`, so a nationally-formatted number can be
   *  normalised server-side. Accepted by the join request; omitted when there
   *  is no number to qualify. */
  phoneCountry?: string;
  email?: string | null;
  service?: string | null;
  party?: number;
  /** The mapped questions' answers (JAI-1911), typed and keyed by question id
   *  (QueueJoinAnswer, CC 0065). At most 50. */
  answers?: WalkInAnswer[];
  /** Same intent -> same key, so a retry after a timeout cannot create a second
   *  entry. The join operation declares `idempotency: 'optional'`. */
  idempotencyKey?: string;
  /** The visitor ticked the data-protection consent (CC consent packet, 2026-10-17).
   *  Sent as `agreedToTerms: true`; left out otherwise. The page blocks the join while
   *  the box is unticked, so only `true` travels. */
  agreedToTerms?: true;
}

export interface WalkInTicket {
  entryId: string;
  queueId: string;
  ticket: string;
  position: number | null;
  peopleAhead: number;
  estWaitMins: number;
  status: string;
  serviceId: string | null;
  recoveredExisting: boolean;
}

export type WalkInJoinResult =
  | { ok: true; ticket: WalkInTicket }
  /**
   * `certainlyNotJoined` separates the two failures a visitor must be told
   * apart. A typed 4xx is the server having READ the request and refused it:
   * nothing was written, and saying so is true. A timeout, a dropped
   * connection or a 5xx is not — the entry may already exist, and telling
   * someone "nothing was saved" there invites them to join again by hand and
   * take a second place in the line. Retrying with the same
   * `idempotencyKey` is the safe move, and the copy says that instead.
   */
  | {
    ok: false; message: string; fields: WalkInRefusedField[]; certainlyNotJoined: boolean;
    /** The server's typed refusal, when it gave one, so the page can tell a
     *  LOST SESSION (`PORTAL_BOOKING_SESSION_REQUIRED`, or the signed-in
     *  door's 404) from a refused detail. Without it the signed-in page
     *  could only ever show an error over inputs it keeps locked. */
    code?: string; status?: number;
  };

function publicErrorMessage(err: unknown, fallback: string): string {
  const e = (err || {}) as Dict;
  const body = rec(e.body);
  const error = rec(body.error);
  const message = str(e.message) || str(error.message);
  return message || fallback;
}

function fieldsFrom(err: unknown): WalkInRefusedField[] {
  const e = (err || {}) as Dict;
  const direct = Array.isArray(e.fields) ? e.fields : null;
  const nested = Array.isArray(rec(rec(e.body).error).fields) ? rec(rec(e.body).error).fields : [];
  return (direct || nested).map((f: Dict) => ({ path: String(f.path || ""), message: String(f.message || "") }));
}

/**
 * A REAL join. Only the public customer mount ever calls this — the designer's
 * preview renders settings and must never create an entry a staffer would have
 * to call out for.
 *
 * The response's `entryId` is OPAQUE and is the customer's only authority for
 * the follow-up reads and for leaving; it is carried through whole and never
 * reconstructed.
 */
/**
 * Join as the SIGNED-IN customer, through this host's verified session.
 *
 * The body carries the visit - desk, service, party - and no identity at all:
 * the server reads who is joining from the session cookie it set when the
 * One-Stop hand-off was redeemed here. That is the whole point of the signed-in
 * path; a name in this body could only ever disagree with the account.
 *
 * The answer shape is the anonymous one, so the ticket, the position and the
 * recovery semantics below are shared rather than re-derived.
 */
export async function joinWalkInQueueAsCustomer(input: {
  slug: string;
  csrf: string;
  queueId: string;
  serviceId?: string;
  party?: number;
  idempotencyKey?: string;
  /** FILL-INS for what the account lacks - the page sends a field here only
   *  when the session supplied no value for it. The server reads the account
   *  first and ignores these wherever the account already answers, so nothing
   *  in this body can re-name who is joining. */
  fillIn?: { name?: string; email?: string; phone?: string; phoneCountry?: string };
  /** Answers to the service's mapped questions (JAI-1911), typed (CC 0065). */
  answers?: WalkInAnswer[];
  /** The data-protection consent, as on the anonymous join. */
  agreedToTerms?: true;
}): Promise<WalkInJoinResult> {
  const fill = input.fillIn || {};
  return joinWalkInQueueThrough(
    () => publishingOps.joinPortalQueueSession(
      input.slug,
      {
        queue_id: input.queueId,
        ...(input.serviceId ? { service_id: input.serviceId } : {}),
        ...(input.party ? { party: Math.max(1, Math.round(Number(input.party) || 1)) } : {}),
        ...(input.answers && input.answers.length ? { answers: input.answers.slice(0, 50) } : {}),
        ...(fill.name ? { name: fill.name } : {}),
        ...(fill.email ? { email: fill.email } : {}),
        ...(fill.phone ? { phone: fill.phone } : {}),
        ...(fill.phone && fill.phoneCountry ? { phone_country: fill.phoneCountry } : {}),
        ...(input.agreedToTerms === true ? { agreedToTerms: true as const } : {}),
      },
      input.csrf,
      // The key is what makes the retry copy below TRUE — the desk replays on
      // it, so a second tap after a timeout returns the same ticket instead of
      // taking a second place in the line. It was declared on this function's
      // parameters and dropped here once; the anonymous door one function down
      // has always forwarded its own.
      { ...publicReadOptions("public"), ...(input.idempotencyKey ? { idempotencyKey: input.idempotencyKey } : {}) },
    ) as Promise<unknown>,
    input.queueId,
  );
}

export async function joinWalkInQueue(input: WalkInJoinInput): Promise<WalkInJoinResult> {
  try {
    const body: Dict = {
      name: input.name,
      journeySlug: input.journeySlug,
      party: Math.max(1, Math.round(Number(input.party) || 1)),
      answers: Array.isArray(input.answers) ? input.answers : [],
    };
    if (input.phone && input.phone.trim()) body.phone = input.phone.trim();
    if (input.phone && input.phone.trim() && input.phoneCountry) body.phoneCountry = String(input.phoneCountry).toUpperCase();
    if (input.email && input.email.trim()) body.email = input.email.trim();
    if (input.service) body.service = input.service;
    if (input.agreedToTerms === true) body.agreedToTerms = true;
    return await joinWalkInQueueThrough(
      () => queueOps.joinPublicQueue(
        input.queueId,
        body as Parameters<typeof queueOps.joinPublicQueue>[1],
        input.idempotencyKey ? { idempotencyKey: input.idempotencyKey } : undefined,
      ) as Promise<unknown>,
      input.queueId,
    );
  } catch (err) {
    return joinWalkInFailure(err);
  }
}

/**
 * Read ONE join answer - anonymous or signed-in. Both doors return the queue's
 * own entry shape, so the ticket, the unreadable-answer case and the retry
 * semantics are defined here once rather than twice; a second copy is how the
 * two paths would drift about what "you may already be in the line" means.
 */
async function joinWalkInQueueThrough(call: () => Promise<unknown>, queueId: string): Promise<WalkInJoinResult> {
  try {
    const raw = (await call()) as Dict;
    // The signed-in write relays the queue's body inside the usual envelope.
    const res = (raw && typeof raw === "object" && raw.data && typeof raw.data === "object" ? raw.data : raw) as Dict;
    const input = { queueId };
    const entryId = str(res.entryId);
    if (!entryId) {
      // A 2xx with no entry id. The write may well have landed — the response
      // is what is unreadable — so this must NOT tell anyone nothing was
      // saved. Retrying carries the same key, which recovers the entry if one
      // exists rather than making a second.
      return {
        ok: false,
        certainlyNotJoined: false,
        message: t("journeys.alljourneysWalkins.message4", "The line answered in a way this page could not read, so your place cannot be shown. You may already be in the queue — try again, or ask at the desk."),
        fields: [],
      };
    }
    const position = typeof res.position === "number" ? res.position : null;
    return {
      ok: true,
      ticket: {
        entryId,
        queueId: str(res.queueId) || input.queueId,
        ticket: str(res.ticket),
        position,
        // DERIVED from the position the server just reported (1-based), not
        // hardcoded: a join at position 4 has three people ahead, and saying
        // zero made the first render of every non-front ticket wrong until the
        // first poll replaced it. The poll is also fired immediately by the
        // caller, so this is only what is shown in between.
        peopleAhead: position != null ? Math.max(0, position - 1) : 0,
        estWaitMins: Number(res.estWaitMins) || 0,
        status: str(res.status, "waiting"),
        serviceId: res.serviceId == null ? null : String(res.serviceId),
        recoveredExisting: res.recoveredExisting === true,
      },
    };
  } catch (err) {
    return joinWalkInFailure(err);
  }
}

/** What a failed join says, for both doors. */
function joinWalkInFailure(err: unknown): WalkInJoinResult {
  const status = Number((err as Dict)?.status || 0);
  // Only a typed 4xx proves the server read the request and wrote nothing.
  // A timeout, a dropped connection or a 5xx proves nothing at all.
  const refusedOutright = status >= 400 && status < 500;
  const code = String((err as Dict)?.code || "").trim();
  return {
    ok: false,
    certainlyNotJoined: refusedOutright,
    ...(code ? { code } : {}),
    ...(status ? { status } : {}),
    // The queue's own refusal, shown verbatim where there is one. A full or
    // closed line is the server's call, never this page's guess.
    // JAI-2020: an AUDIENCE_FORBIDDEN refusal reads the approved sentence,
    // never the server's message or its reason code.
    /* Closed or full (CC 0356): worded from the code and params, never the registry's message. */
    message: queueRefusalText(err) ?? audienceRefusalCopy(t, err) ?? publicErrorMessage(
      err,
      refusedOutright
        ? t("journeys.alljourneysWalkins.joinRefused", "The line refused your details. Nothing was saved - please check them and try again.")
        : t("journeys.alljourneysWalkins.joinUnreachable", "The line could not be reached. You may or may not be in the queue - try again, and if you are already in it you will get the same ticket back."),
    ),
    fields: fieldsFrom(err),
  };
}

export type WalkInPositionResult =
  | { ok: true; ticket: WalkInTicket }
  | { ok: false; message: string; gone: boolean };

/** Poll one entry's own position. A 404 means the entry is gone (left, or the
 *  queue was reset) and IS terminal; anything else is transient and the caller
 *  keeps its credentials and tries again. */
export async function pollWalkInPosition(queueId: string, entryId: string): Promise<WalkInPositionResult> {
  try {
    const res = (await queueOps.getPublicQueuePosition(queueId, entryId)) as unknown as Dict;
    return {
      ok: true,
      ticket: {
        entryId: str(res.entryId) || entryId,
        queueId,
        ticket: str(res.ticket),
        position: typeof res.position === "number" ? res.position : null,
        peopleAhead: Number(res.peopleAhead) || 0,
        estWaitMins: Number(res.estWaitMins) || 0,
        status: str(res.status, "waiting"),
        // The catalogue id picked at join (or the queue's own service); null when
        // none. Optional on the read for a worker built before the field existed.
        serviceId: str(res.serviceId) || null,
        recoveredExisting: false,
      },
    };
  } catch (err) {
    const status = Number((err as Dict)?.status || 0);
    return { ok: false, gone: status === 404, message: publicErrorMessage(err, t("journeys.alljourneysWalkins.placeRefreshFailed", "Your place could not be refreshed just now.")) };
  }
}

export type WalkInLeaveResult = { ok: true } | { ok: false; message: string };

/** A REAL leave. The "you have left the line" screen is shown only after this
 *  resolves — a local flag would tell someone they had given up their place
 *  while the desk still had them waiting. */
export async function leaveWalkInQueue(queueId: string, entryId: string): Promise<WalkInLeaveResult> {
  try {
    await queueOps.leavePublicQueue(queueId, entryId);
    return { ok: true };
  } catch (err) {
    return { ok: false, message: publicErrorMessage(err, t("journeys.alljourneysWalkins.leaveFailed", "You could not be taken out of the line just now. You are still in it — please try again.")) };
  }
}

/* ── The reload credential ──────────────────────────────────────────────── */

export interface WalkInCredentials { queueId: string; entryId: string }

const QUEUE_PARAM = "q";
const ENTRY_PARAM = "e";

/** What a reload can recover: the queue and the opaque entry id, and NOTHING
 *  else. No name, phone or email is ever put in the URL. */
export function readWalkInCredentials(search: string): WalkInCredentials | null {
  try {
    const params = new URLSearchParams(String(search || ""));
    const queueId = (params.get(QUEUE_PARAM) || "").trim();
    const entryId = (params.get(ENTRY_PARAM) || "").trim();
    return queueId && entryId ? { queueId, entryId } : null;
  } catch {
    return null;
  }
}

/**
 * Keep the ticket recoverable across a reload.
 *
 * `replaceState`, not a navigation, and the entry id is a CAPABILITY: anyone
 * holding it can read this person's place and remove them from the line. So the
 * page also sets a `no-referrer` policy before the id is in the address bar —
 * without it every outbound click leaks the full URL, capability included, in
 * the `Referer` header.
 */
export function writeWalkInCredentials(creds: WalkInCredentials): void {
  try {
    if (typeof window === "undefined" || !window.history || !window.history.replaceState) return;
    protectWalkInReferrer();
    const url = new URL(window.location.href);
    url.searchParams.set(QUEUE_PARAM, creds.queueId);
    url.searchParams.set(ENTRY_PARAM, creds.entryId);
    window.history.replaceState(window.history.state, "", url.toString());
  } catch { /* an address bar this page cannot rewrite is not a failed join */ }
}

export function clearWalkInCredentials(): void {
  try {
    if (typeof window === "undefined" || !window.history || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.delete(QUEUE_PARAM);
    url.searchParams.delete(ENTRY_PARAM);
    window.history.replaceState(window.history.state, "", url.toString());
  } catch { /* see above */ }
}

/** A `<meta name="referrer" content="no-referrer">`, added once. The entry id
 *  lives in the query string, so any link, image or form on this page would
 *  otherwise hand it to a third party. */
export function protectWalkInReferrer(): void {
  try {
    if (typeof document === "undefined") return;
    if (document.querySelector('meta[name="referrer"][data-walkin-referrer]')) return;
    const meta = document.createElement("meta");
    meta.setAttribute("name", "referrer");
    meta.setAttribute("content", "no-referrer");
    meta.setAttribute("data-walkin-referrer", "");
    document.head.appendChild(meta);
  } catch { /* non-DOM host */ }
}

/**
 * What the ticket's QR encodes: THIS page's own status URL, same origin,
 * carrying only the queue and entry ids already in the address bar.
 *
 * Never a made-up code and never a third-party host. In preview there is no
 * entry, so there is no URL to encode and the panel says so rather than drawing
 * a scannable square that resolves to nothing.
 */
export function walkInStatusUrl(creds: WalkInCredentials | null): string {
  if (!creds) return "";
  try {
    const url = new URL(window.location.href);
    url.search = "";
    url.searchParams.set(QUEUE_PARAM, creds.queueId);
    url.searchParams.set(ENTRY_PARAM, creds.entryId);
    return url.toString();
  } catch {
    return "";
  }
}

/** A stable idempotency key for ONE join intent, so a retry after a timeout
 *  recovers the same entry instead of creating a second one. */
export function newWalkInIntentKey(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch { /* fall through */ }
  return "wi-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

/* ── Wait formatting ────────────────────────────────────────────────────── */

/** The wait as the page is configured to state it. `position` deliberately
 *  returns "" — the caller shows the position tile instead of a duration. */
export function waitLabel(minutes: number, display: WalkInConfig["qWaitDisplay"]): string {
  const n = Math.max(0, Math.round(Number(minutes) || 0));
  if (display === "position") return "";
  if (display === "range") {
    if (n <= 0) return t("journeys.alljourneysWalkins.waitNone", "No wait");
    const low = Math.max(1, Math.round(n * 0.75));
    const high = Math.max(low + 1, Math.round(n * 1.25));
    return t("journeys.alljourneysWalkins.waitRange", "{low}–{high} min", { low, high });
  }
  return n <= 0 ? t("journeys.alljourneysWalkins.waitNone", "No wait") : t("journeys.alljourneysWalkins.waitMinutes", "{minutes} min", { minutes: n });
}

/**
 * The per-service wait line, prefix included.
 *
 * The prefix is authored copy that introduces a DURATION ("Wait about" +
 * "12 min"). At zero there is no duration — `waitLabel` says "No wait", a
 * complete sentence — and gluing the prefix in front of it produced
 * "Wait about No wait" on every idle desk. The prefix is dropped in exactly
 * that case; every other value keeps it, and an unknown wait (no number from
 * the queue) still renders nothing at all, because the caller passes `null`
 * and never reaches here.
 */
export function serviceWaitLine(minutes: number, display: WalkInConfig["qWaitDisplay"], prefix: string): string {
  // The per-service line has no position tile to defer to, so `position` is
  // stated as a duration here — same substitution the caller already made.
  const label = waitLabel(minutes, display === "position" ? "exact" : display);
  if (!label) return "";
  const n = Math.max(0, Math.round(Number(minutes) || 0));
  if (n <= 0) return label;
  return prefix ? prefix + " " + label : label;
}
